import BigNumber from 'bignumber.js';
import {
  NumberInput as MantineNumberInput,
  NumberInputProps,
  Button,
} from '@mantine/core';
import cn from 'clsx';
import { useRef } from 'react';
import { fixLeadingZero } from 'src/utils';
import { MAX_DECIMALS_DISPLAY } from 'src/constants';
import { Token } from 'src/components/Token/Token';

export function NumberInput({
  value,
  label,
  token,
  rightSection,
  rightSectionWidth,
  inputProps,
  isLoading,
  onChange,
  onMaxClick,
}: {
  value?: string | number | BigNumber;
  label?: React.ReactNode;
  token?: string;
  rightSection?: React.ReactNode;
  rightSectionWidth?: number;
  inputProps?: NumberInputProps;
  allowDecimals?: boolean;
  isLoading?: boolean;
  onChange: (value?: string) => void;
  onMaxClick?: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <MantineNumberInput
      label={label}
      hideControls
      leftSection={
        token ? (
          <Token.Icon symbol={token} size={32} boxClassName="ml-4" />
        ) : undefined
      }
      leftSectionWidth={token ? 48 : 0}
      rightSection={rightSection}
      rightSectionWidth={rightSectionWidth}
      rightSectionProps={{ className: 'px-4 justify-end' }}
      {...(onMaxClick && {
        rightSection: (
          <Button
            h={24}
            px={12}
            fz={14}
            className="rounded-[6px] text-main0"
            onClick={() => {
              onMaxClick();
            }}
          >
            Max
          </Button>
        ),
        rightSectionWidth: 130,
      })}
      min={0}
      max={999_999_999_999}
      clampBehavior="strict"
      defaultValue={0}
      thousandSeparator=","
      decimalScale={MAX_DECIMALS_DISPLAY}
      allowNegative={false}
      ref={inputRef}
      size="md"
      placeholder="0.0"
      trimLeadingZeroesOnBlur={false}
      {...inputProps}
      className={cn(
        inputProps?.className,
        isLoading && 'pointer-events-none animate-pulse'
      )}
      classNames={{
        ...inputProps?.classNames,
        input: cn(token && 'pl-14'),
      }}
      value={String(value) || '0'}
      onChange={(v) => {
        if (v === '.') {
          return onChange('0.');
        }

        if (!v || v === 'undefined') {
          onChange(undefined);
        } else {
          onChange(fixLeadingZero(String(v)));
        }

        if (v === '' && inputRef.current) {
          inputRef.current.blur();
          setTimeout(() => {
            inputRef.current?.focus();
          }, 0);
        }
      }}
    />
  );
}
